import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import useForm from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import FormSection from '../../../../components/FormComponents/FormSection';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import { searchVehicleSchema } from '../../../../validations/contract/roadCare';
import {
  ROAD_CARE_SEARCH_VEHICLE_BACKEND_ERROR_MESSAGES,
  ROAD_CARE_SEARCH_VEHICLE_REQUIRED_FIELDS,
} from '../../../../constants/roadCare';
import { searchEkengVehicle, setSearchVehicleFieldsData } from '../../../../redux/modules/roadCareContract';
import CustomErrorMessage from '../../../../components/CustomErrorMessage';
import VehicleInfo from '../../../../components/FormComponents/VehicleInfo/VehicleInfo';
import { bindRoadCareVehicleFormData } from '../../../../helpers/roadCare/bindData';
import { getRoadCareTransportReleasedYears } from '../../../../helpers/roadCare/roadCareCalcHelper';
import Navigation from './Navigation';
import SearchVehicle from '../../../../components/SearchVehicle';
import InfoBlock from '../../../../components/InfoBlock';
import ScrollToTopPage from '../../../../components/ScrollToTopPage';

import VehicleInfoIcon from '../../../../img/formSectionIcons/vehicleInfo.svg';
import SearchVehicleIcon from '../../../../img/formSectionIcons/searchVehicleIcon.svg';
import useStyles from './makeStyles';

const SearchPage = ({ params, goPrevious, blockNextSteps, goNext }) => {
  const {
    setStepNavigationBox,
    isDeviceLgSize,
    handleOpenMobileSummary,
    totalPrice,
    signerBackendErrors,
  } = params;
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    ekengVehicleErrors,
    ekengVehicleData,
    searchVehicleFieldsData,
    isFetchingEkengVehicleData,
  } = useSelector((state) => state.roadCareContract);
  const { transportUsageTypes } = useSelector((state) => state.vehiclesReducer);
  const vehicleInfoElement = useRef(null);

  const {
    register,
    setValue,
    errors,
    clearError,
    getValues,
    handleSubmit,
    watch,
  } = useForm({
    defaultValues: {
      plateNumber: searchVehicleFieldsData.plateNumber,
      passport: searchVehicleFieldsData.passport,
      hasAgreed: searchVehicleFieldsData.hasAgreed,
    },
    validationSchema: searchVehicleSchema,
    mode: 'onBlur',
  });

  watch(ROAD_CARE_SEARCH_VEHICLE_REQUIRED_FIELDS);
  useRegisterValidationFields(ROAD_CARE_SEARCH_VEHICLE_REQUIRED_FIELDS, register);

  const vehicleData = bindRoadCareVehicleFormData(ekengVehicleData);

  useEffect(() => {
    if (!isEmpty(ekengVehicleData) && vehicleInfoElement.current) {
      vehicleInfoElement.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [ekengVehicleData]);

  const handleSearchVehicle = () => {
    dispatch(setSearchVehicleFieldsData({
      passport: getValues().passport,
      plateNumber: getValues().plateNumber,
      hasAgreed: getValues().hasAgreed,
    }));

    dispatch(searchEkengVehicle({
      techPassport: getValues().passport,
      plateNumber: getValues().plateNumber,
    }));
  };

  const showError = useMemo(() => ekengVehicleErrors
        && ROAD_CARE_SEARCH_VEHICLE_BACKEND_ERROR_MESSAGES.includes(ekengVehicleErrors.message),
  [ekengVehicleErrors]);

  return (
    <div>
      {
        !signerBackendErrors && <ScrollToTopPage withDelay />
      }
      <FormSection title="transportation.vehicleSearchTitle" icon={SearchVehicleIcon}>
        {
            showError && (
            <Grid className={classes.customErrorMessage}>
              <CustomErrorMessage
                customErrorCode={t(`errorNotifications.${ekengVehicleErrors.message}`)}
              />
            </Grid>
            )
        }
        <SearchVehicle
          formProps={{ errors, clearError, getValues, setValue, register, handleSubmit }}
          handleSearchVehicle={handleSearchVehicle}
          classes={classes}
          disableSearchButton={!getValues().hasAgreed}
          isFetchingEkengVehicleData={isFetchingEkengVehicleData}
          infoBlock={(
            <InfoBlock
              formProps={{
                register,
                errors,
              }}
              handleAgreementClick={(event) => {
                const { checked } = event.target;
                setValue('hasAgreed', checked);
                dispatch(setSearchVehicleFieldsData({
                  passport: getValues().passport,
                  plateNumber: getValues().plateNumber,
                  hasAgreed: checked,
                }));
                if (!checked) {
                  blockNextSteps();
                }
              }}
              hasAgreed={getValues().hasAgreed}
              agreementText="roadCareContract.agreementDeclaration"
            />
          )}
        />
      </FormSection>
      <div ref={vehicleInfoElement}>
        {
        !isEmpty(ekengVehicleData) && (
        <FormSection title="transportation.vehicleInfoTile" icon={VehicleInfoIcon}>
          <VehicleInfo
            formProps={{ errors, clearError }}
            formValues={vehicleData}
            transportUsageTypes={transportUsageTypes ?? []}
            vehicleManufactures={[]}
            modelNames={[]}
            releaseYears={getRoadCareTransportReleasedYears()}
          />
        </FormSection>
        )
      }
      </div>
      <Navigation
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        handleOpenMobileSummary={handleOpenMobileSummary}
        goNext={goNext}
        goPrevious={goPrevious}
        totalPrice={totalPrice}
        isFormValid={
          ((vehicleData.vin && !isEmpty(ekengVehicleData)) && getValues().hasAgreed) || false
        }
      />
    </div>
  );
};

SearchPage.propTypes = {
  params: PropTypes.shape({
    setStepNavigationBox: PropTypes.func.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    isDeviceLgSize: PropTypes.bool.isRequired,
    totalPrice: PropTypes.number.isRequired,
    signerBackendErrors: PropTypes.shape({}),
  }).isRequired,
  goPrevious: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
  blockNextSteps: PropTypes.func.isRequired,
};

export default SearchPage;
